import trimStart from 'lodash/trimStart'
import { useCmsFetch } from '@/composables/craft-fetch'
import { useCacheStore } from '~/store/cache'

export const usePageStore = defineStore('page', () => {
  const cacheStore = useCacheStore()
  const cmsFetch = useCmsFetch()

  const page = ref(undefined)

  function setPage(newPage: any) {
    page.value = newPage
  }

  async function fetchUri({ site, uri, push = false, options = {} }: { site: string; uri: string; push?: boolean; options?: object }) {
    /**
     * [DB-748] Only trim leading slashes: a trailing slash must reach the CMS as-is, so the CMS can
     * decide to redirect to the canonical URL without the slash. A URI with a trailing slash will
     * not match any entry, after which the SEO redirect flow asks the CMS for a redirect.
     */
    const normalizedUri = trimStart(uri, '/')

    const key = `page;site:${site};uri:${normalizedUri}`
    const ttl = 300

    /**
     * [DB-748] The URI is passed as a query parameter instead of in the URL path: a URI with a
     * trailing slash would otherwise produce a request URL ending in `/.json`, a hidden file
     * ("dotfile") URL that hosting setups commonly block. `encodeURI` keeps the slashes readable,
     * so the request URL matches the purge targets configured in the relation-manager settings.
     */
    const fetcher = async () => {
      return await cmsFetch(normalizedUri ? `/api/site/${site}/page.json?uri=${encodeURI(normalizedUri)}` : `/api/site/${site}/home.json`, { ...options })
    }

    const result = await cacheStore.fetch({ key, ttl, fetcher })
    if (push && result) {
      page.value = result
    }
    return result
  }

  async function fetchHome({ site, push, options }: { site: string; push: boolean; options: any }) {
    const key = `page;site:${site};home`
    const ttl = 300

    const fetcher = async () => {
      return await cmsFetch(`/api/site/${site}/home.json`, options)
    }

    const result = await cacheStore.fetch({ key, ttl, fetcher })
    if (push && result) {
      page.value = result
    }
    return result
  }

  async function fetchError({ site, sourceSection, statusCode, push }: { site: string; sourceSection: string; statusCode: number; push: boolean }) {
    const key = `page;site:${site};statusCode:${statusCode};sourceSection:${sourceSection}`
    const ttl = 300

    const fetcher = async () => {
      return await cmsFetch(`/api/site/${site}/error/${statusCode}-${sourceSection}.json`)
    }

    const result = await cacheStore.fetch({ key, ttl, fetcher })
    if (push && result) {
      page.value = result
    }
    return result
  }

  return { page, setPage, fetchUri, fetchHome, fetchError }
})
